
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { StockTrackingChartInterval } from '@toro/common/shared/components/charts/stock-tracking-chart/stock-tracking-chart-interval';
import { DisposableSafelly } from '@toro/common/shared/components/disposable-safelly/disposable-safelly.component';
import { OrderMarketTypeEnum } from '@toro/common/shared/enum/orders/market-type.enum';
import { OrderSideEnum } from '@toro/common/shared/enum/orders/side.enum';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { Exports } from '@toro/common/shared/resources/consts';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';
import { ApplicationMessagesAction } from '@toro/common/shared/state-management/application-messages/application-message.action';
import { StocksMarketEffects } from '@toro/common/shared/state-management/stocks-market/stocks-market.effects';
import * as highcharts from 'highcharts/highstock';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Observable, Subscription, timer } from 'rxjs';
import { debounceTime, filter, first, map, throttleTime } from 'rxjs/operators';
import { StockTrackingChartsWraperComponent } from '../stock-tracking-charts-wraper/stock-tracking-charts-wraper.component';

const DISABLED = { enabled: false };
const NO_TEXT = { text: ''};
const GAIN_COLOR = '#00c853';
const LOSS_COLOR = '#f44336';
const CURRENT_COLOR = '#222222';
const NO_VISIBLE_COLOR = 'rgba(0,0,0,0)';
const ADITIONAL_CHART_SERIES_SPACING_POSITIVE = 0.4;
const ADITIONAL_CHART_SERIES_SPACING_NEGATIVE = 1;

export enum PlotLine {
    StopGain = 'gain',
    StopLoss = 'stop',
    Current = 'current',
}

@AutoUnsubscribe()
@Component({
    selector: 'tr-tracking-chart',
    styleUrls: ['./tr-tracking-chart.component.scss'],
    templateUrl: './tr-tracking-chart.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class TrTrackingChartComponent extends DisposableSafelly implements AfterViewInit, OnDestroy {

    @Input() public code: string;
    @Input() public stopGainLabel: string;
    @Input() public stopLossLabel: string;
    @Input() public interval: StockTrackingChartInterval;
    @Input() public foreground: boolean;
    @Input() public market: OrderMarketTypeEnum;
    @Input() public side = OrderSideEnum.Compra;

    @Input()
    set stopLoss(value: number) {
        this.auxStopLoss = value;
        this.updatePlottedLine(PlotLine.StopLoss, value);
    }
    get stopLoss(): number {
        return this.auxStopLoss;
    }

    @Input()
    set stopGain(value: number) {
        this.auxStopGain = value;
        this.updatePlottedLine(PlotLine.StopGain, value);
    }
    get stopGain(): number {
        return this.auxStopGain;
    }

    @Input()
    set currentResultLabel(value: string) {
        if (value) {
            this.auxCurrentLabel = value;
            this.updatePlottedLine(PlotLine.Current);
        }
    }

    get currentResultLabel(): string {
        if (this.auxCurrentLabel) {
            if (this.planned) {
                return `${this.auxCurrentLabel} ${ this.utilitiesService.isMobile() ? '' : `(${this.formattedCurrentLabel})` }`;
            } else {
                return this.formattedCurrentLabel;
            }
        }
        return this.formattedCurrentLabel;
    }

    @ViewChild('container',  {static: true}) public container: ElementRef;

    public get plotted() {
        return !!this.chartInstance;
    }

    private set quote(value: number) {
        this.auxQuote = value;
        this.updatePlottedLine(PlotLine.Current, this.quote);
    }

    private get quote(): number {
        return this.auxQuote;
    }

    private get normalizedStockId() {
        return this.code.toUpperCase();
    }

    private get normalizedUpperCaseStockId() {
        return this.code.toUpperCase();
    }

    public get planned() {
        return this.stopLoss && this.stopGain;
    }

    public get reverse() {
        return this.side === OrderSideEnum.Venda;
    }

    private get formattedCurrentLabel(): string {

        if (this.market !== OrderMarketTypeEnum.Future) {
            return this.currencyPipe.transform(this.auxQuote, 'BRL', true);
        }

        return `${this.decimalPipe.transform(this.auxQuote, '3.2-5')} pts`;
    }

    public values: any[];
    public chartInstance: any;
    public quote$: Observable<any>;
    public instrumentQuoteSubscription: Subscription;
    public error: string;
    private chartHover: boolean;
    private plotLines = new Array<any>();
    private auxQuote: number;
    private auxStopGain: number;
    private auxStopLoss: number;
    private auxCurrentLabel: string;
    private enableMarketSubscription: Subscription;
    private lastQuote: number;
    private lastQuoteDate: string;

    constructor(
        private store: Store<AppState>,
        private currencyPipe: CurrencyPipe,
        private decimalPipe: DecimalPipe,
        private wrapper: StockTrackingChartsWraperComponent,
        private stocksMarketEffects: StocksMarketEffects,
        private utilitiesService: UtilitiesService,
    ) {
        super();

        wrapper.addChart(this);

        // dont use utc
        highcharts.setOptions({
            time: { useUTC: false },
        });
        // translate
        highcharts.setOptions({
            lang: Exports.highchartsLang,
        });

        this.quote$ = this.store.select((s) => s.stocksExchange.stocksMarket)
            .pipe(debounceTime(1));
    }

    public ngAfterViewInit() {
        // get initial data
        this.disposableSubscriptions.push(
            this.store.select((s) => s.stocksExchange.stocksMarket.quoteHistory)
                .pipe(
                    first((state) => state[this.normalizedStockId] && state[this.normalizedStockId][this.interval] !== undefined),
                    map((state) => state[this.normalizedStockId][this.interval]),
                )
                .subscribe((data) => {

                    this.values = data;

                    if (data.length > 0) {
                        this.lastQuoteDate = data[data.length - 1].quoteDate;
                    }

                    // Plota o gráfico se não foi protado ainda
                    if ( (this.plotted !== true && this.foreground) || this.interval === StockTrackingChartInterval.OneHour ) {
                        this.plotChart();
                    }
            }),
        );

        // erro ao tentar recuperar os dados
        this.stocksMarketEffects.chartDataRetrievingError$.subscribe((action: any) => {
            if (this.foreground) {
                this.store.dispatch({type: ApplicationMessagesAction.genericError, payload: { message: 'Erro ao processar o gráfico'}});
            }
            this.error = action.payload;
        });
    }

    /**
     * Cria e retorna uma linelabel (conteúdo)
     * @param y valor para o eixo y
     * @param template template para incorporar a line label
     * @param type Tipo de plotline
     * @param additionalClass classe adicional
     */
    public getLineLabel(y: number, template: string, type: PlotLine, additionalClass?: string) {
        // if(this.reverse){
        //     if(type == PlotLine.StopGain){
        //         type = PlotLine.StopLoss;
        //     }else{
        //         if(type == PlotLine.StopLoss)
        //             type = PlotLine.StopGain;
        //     }
        // }
        return ` <span class='font-small--medium chart-plotline chart-plotline-label ${additionalClass} chart-plotline-label--${type}'>
            ${template}
        </span>`;
    }

    /**
     * Formatter de valores para o eixo x
     */
    public xAxisLabelsFormatter(): () => void {

        const interval = this.interval;

        return function() {

            // Não exibir o último e o primeiro lable de tick
            if (this.isFirst || this.isLast || !this.pos) return null;

            const index = this.chart.series[0].processedYData.findIndex((i) => i == null);
            const relatedYValue = this.chart.series[0].processedXData[index];
            // Não mostrar ticks que são do respiro de padding a direita da área de plot
            if (this.pos > relatedYValue) return null;

            switch (interval) {
                case StockTrackingChartInterval.OneHour:
                case StockTrackingChartInterval.OneDay:
                    return highcharts.dateFormat('%H:%M', this.value);

                case StockTrackingChartInterval.OneWeek:
                case StockTrackingChartInterval.OneMonth:
                case StockTrackingChartInterval.TwoMonths:
                    return highcharts.dateFormat('%e de %b', this.value);

                case StockTrackingChartInterval.OneYear:
                    return highcharts.dateFormat('%b de %Y', this.value);

                default:
                    return highcharts.dateFormat('%Y', this.value);

            }
        };
    }

    /**
     * @description Atualiza as linhas plotadas no gráfico
     * @param {PlotLine} lineType
     * @param {number} value
     */
    public updatePlottedLine(lineType: PlotLine, value: number = null)  {
        if (this.plotted && value) {
            if (!this.isValidChartInstance()) { return; }
            const lines = this.chartInstance.series[0].yAxis.plotLinesAndBands;
            const line = lines.find((lineParam) => lineParam.options.type === lineType);
            const newLine = this.createPlotLineLabel(lineType, value);
            const newLines = [
                ...lines
                    .filter((plotline) => plotline.options.type !== lineType)
                    .map((plotline) => plotline.options),
                newLine,
            ];
            // this.chartInstance.series[0].yAxis.addPlotLine(newLine);

            if (this.chartInstance.yAxis[0]) {
                this.chartInstance.yAxis[0].update({ plotLines: newLines });
                this.chartInstance.yAxis[0].update(
                    {
                        min: this.computeYAxisMinValue(),
                        max: this.computeYAxisMaxValue(),
                    },
                );
            }
        }
    }

    /**
     * @description Cria label para uma linha
     * @param {PlotLine} lineType
     * @param {number} value
     */
    public createPlotLineLabel(lineType: PlotLine, value: number) {
        switch (lineType) {

            // Label para o valor atual
            case PlotLine.Current: {
                return this.getPlotLineLabel(value, CURRENT_COLOR, this.currentResultLabel, lineType);
            }

            // Label para o valor de gain
            case PlotLine.StopGain: {
                return this.getPlotLineLabel(value, GAIN_COLOR, this.stopGainLabel, lineType);
            }

            // Label para o valor de loss
            case PlotLine.StopLoss: {
                return this.getPlotLineLabel(value, LOSS_COLOR, this.stopLossLabel, lineType);
            }

        }
    }

    /**
     * Atualiza o gráfico com base no novo valor recebido
     * @param point ponto a ser adicionado ao gráfico
     */
    public updateChartData(point: any, newPoint = false) {

        if (!this.isValidChartInstance()) { return; }

        const seriesData = this.chartInstance.series[0];

        // Atualiza a plotline (remove e a adiciona novamente)
        // Index do último elemento
        const index = this.chartInstance.series[0].yAxis.plotLinesAndBands.length - 1;
        // Última plotline (valor atual)
        const lstPlotLine = this.chartInstance.series[0].yAxis.plotLinesAndBands[index];
        lstPlotLine.destroy();

        // Nova plotLine
        const plotLine = this.getPlotLineLabel(point.y, NO_VISIBLE_COLOR, this.currentResultLabel, PlotLine.Current);
        this.chartInstance.series[0].yAxis.addPlotLine(plotLine);

        const firstInvalidItemIndex = seriesData.data.findIndex((i) => i.y == null);

        if (seriesData.data[firstInvalidItemIndex - 1]) {
            seriesData.data[firstInvalidItemIndex - 1].update({ marker: { enabled: false } }, true, false);
        }

        // Adiciona o ponto e desabilita o marker
        if (newPoint) {
            seriesData.addPoint({
                x: point.x,
                y: point.y,
                marker: {
                    enabled: false,
                },
            }, true, true);
        } else if (seriesData.data[firstInvalidItemIndex - 1]) {
            seriesData.data[firstInvalidItemIndex - 1].update(point.y);
            this.chartInstance.linkSeries();
        }

        // Habilita o marker após a linha se movimentar
        if (!this.chartHover) {
            this.enableMarketSubscription =
                timer(1000)
                .subscribe((data) => {
                    if (seriesData.data[firstInvalidItemIndex - 1]) {
                        seriesData.data[firstInvalidItemIndex - 1]
                            .update({ marker: { enabled: true, symbol: 'url(assets/images/blue-ball.svg)' } }, true, false);
                    }
                });
        }

        this.showLastPointMarker();

    }

    /**
     * Inicia o tracking de cotação para o ativo do gráfico
     */
    public startTrackingInstrumentQuote() {

        const expectedTimeDiff = {
            [StockTrackingChartInterval.OneHour]    : 1,
            [StockTrackingChartInterval.OneDay]     : 15,
            [StockTrackingChartInterval.OneWeek]    : 1,
            [StockTrackingChartInterval.OneMonth]   : 1,
            [StockTrackingChartInterval.OneYear]    : 1,
            [StockTrackingChartInterval.FiveYears]  : 5,
        };

        let timeUnit;
        if (this.interval === StockTrackingChartInterval.OneHour || this.interval === StockTrackingChartInterval.OneDay) {
            timeUnit = 'minute';
        } else {
            timeUnit = 'day';
        }

        const getQuoteValue = (item) => item.PriceLastTrade || item.OpeningPrice || item.ClosingPricePreviousDay;

        // Inicia o tracking
        this.instrumentQuoteSubscription = this.store
            .select((state) => state.stocksExchange.stocksMarket.quote)
            .pipe(
              map((quotes) => quotes[this.code]),
              filter((quote) => !!quote),
              throttleTime(10),
            )
            .subscribe((quote) => {

                this.quote = getQuoteValue(quote);

                // remover ternário quando começar a vir esta informação do signalr
                quote.DateTimeLastTrade = quote.DateTimeLastTrade || (new Date()).toISOString();

                const time = (new Date(quote.DateTimeLastTrade)).getTime();
                const point = {y: this.quote, x: time};

                const quoteDiffTime = this.utilitiesService.diffTime(new Date(quote.DateTimeLastTrade), new Date(this.lastQuoteDate), timeUnit);

                if (quoteDiffTime >= expectedTimeDiff[this.interval]) {

                    // Adiciona o valor ao gráfico (novo ponto)
                    this.updateChartData(point, true);
                    this.lastQuoteDate = quote.DateTimeLastTrade;

                } else {

                    // Atualiza o último ponto do gráfico
                    this.updateChartData(point, false);

                }
                // verifica se o valor atingiu o gain ou o stop
                this.checkValue(point);
        });
    }

    /**
     * Realiza toggle do último marker
     * @param show mostrar/não mostrar
     */
    public showLastPointMarker(show = true) {
        if (!this.isValidChartInstance()) { return; }
        const serie = this.chartInstance.series[0];

        if (serie && serie.points.length) {
                timer(500)
                .subscribe((r) => {
                    const index = serie.points.findIndex((i) => i.y == null);
                    if (serie.points[index - 1]) {
                        serie.points[index - 1].update({
                            marker: {
                                enabled: show,
                            },
                        }, true, false);
                    }
                });
        }
    }

    /**
     * Verifica se o novo valor recebido atingiu um dos limites caso haja planejamento
     * @param point ponto de análise (x,y)
     */
    public checkValue(point: any) {
        if (!this.planned) return false;

        // reverse é TRUE para venda
        const stopHitted = this.reverse ? point.y >= this.stopLoss : point.y <= this.stopLoss;
        const gainHitted = this.reverse ? point.y <= this.stopGain : point.y >= this.stopGain;

        if (stopHitted) {
            this.wrapper.onStopHitHandler(point.y);
        }

        if (gainHitted) {
            this.wrapper.onGainHitHandler(point.y);
        }
    }
    /**
     * Resetar o zoom do gráfico
     */
    public resetZoom(): void {
        this.chartInstance.zoomOut();
    }

    // tslint:disable-next-line:no-empty
    public ngOnDestroy() {}

    private getChartSeries(): number[][] {
        const sortedValues = [...this.values].sort(this.sortValuesMapFunction);
        const formattedValues = [];
        for(const item of sortedValues) {
            if(item.quoteDate && item.valueLastTrade) {
                const newItem = this.normalizeData(item);
                formattedValues.push(newItem);
            }
        }

        return formattedValues;
    }

    /**
     * Plota o gráfico
     */
    private plotChart(): void {

        if (this.values.length === 0) {
            return;
        }

        const lastValue = this.values[this.values.length - 1] ? this.values[this.values.length - 1][0] : 0;
        const step =  this.values[this.values.length - 2] ? this.values[this.values.length - 2][0] : 0;
        const paddingRight = Math.round(this.values.length / 100 * 30);

        // padding a direita da plota area
        for (let i = 1; i <= paddingRight; i++) {
            this.values.push([ lastValue + (step * i), null]);
        }

        this.plotLines = [];
        const value = this.values[this.values.length - 1][1];
        this.quote = value;
        // this.values.push([this.values[0][1] + 6000, null];
        this.plotLines.unshift(this.getPlotLineLabel(value, NO_VISIBLE_COLOR, this.formattedCurrentLabel, PlotLine.Current));
        this.plotLines.unshift(this.getPlotLineLabel(Infinity, NO_VISIBLE_COLOR, this.stopGainLabel, PlotLine.StopGain));
        this.plotLines.unshift( this.getPlotLineLabel(-Infinity, NO_VISIBLE_COLOR, this.stopLossLabel, PlotLine.StopLoss));
        const startTrackingInstrument = () => this.startTrackingInstrumentQuote();
        const showLastPointMarker = () => this.showLastPointMarker();
        const setChartInstance = (instance) => this.chartInstance = instance;

        this.values = this.getChartSeries();

        // Create the chart
        this.chartInstance = highcharts.stockChart(this.container.nativeElement, {
            chart: {
                marginRight: 8,
                marginTop: 40,
                style: {
                    fontSize: '14px',
                    fontFamily: 'Roboto',
                },
                events: {
                    load: () => {
                        setChartInstance(this);
                        showLastPointMarker();
                        startTrackingInstrument();
                    },
                },
                resetZoomButton: {
                    theme: {
                        display: 'none',
                    },
                },
            },
            rangeSelector: DISABLED,
            navigator: DISABLED,
            title: {
                text: '',
            },
            exporting: DISABLED,
            credits: DISABLED,
            scrollbar: DISABLED,
            tooltip: {
                split: false,
                positioner: this.tooltipPositioner(),
                formatter: this.tooltipFormatter(),
                useHTML: true,
            },
            yAxis: {
                max: null,
                min: null,
                opposite: false,
                endOnTick: true,
                startOnTick: true,
                gridLineWidth: 2,
                gridLineColor: '#D1D5D7',
                labels: {
                    style: {
                        fontFamily: 'Roboto',
                        fontSize: '14px',
                    },
                    useHTML: true,
                    reserveSpace: true,
                    formatter: this.yAxisFormatter(this.decimalPipe),
                },
                minPadding: 0.1,
                maxPadding: 0.4,
                plotLines: this.plotLines,
                offset: 1,
            },
            xAxis: {
                minPadding: 0.2,
                maxPadding: 0.2,
            },
            // breaks: [
            //     {
            //         from: this.values[this.values.length - 1],
            //         to: this.values[this.values.length - 1][0] + 600000,
            //         repeat: 24 * 36e5,
            //     },
            // ],
            series: [{
                type: 'line',
                name: 'Cotação',
                cropThreshold: 1000,
                data: this.values,
                marker: {
                    enabled: false,
                    symbol: 'url(assets/images/blue-ball.svg)',
                    fillColor: '#FFFFFF',
                    radius: 0,
                },
                color: CURRENT_COLOR,
            }],

            // plot options
            plotOptions: {
                series: {
                    cropThreshold: 1000,
                    shadow: false,
                    borderWidth: 0,
                    marker: {
                        enabled: false,
                    },
                    lineWidth: 3,
                    states: {
                        hover: {
                            lineWidth: 3,
                        },
                    },
                    events: {
                        // esconde o marker fixo quando o mouse entra no gráfico
                        mouseOver: (event) => {

                            this.showLastPointMarker(false);
                            this.chartHover = true;
                        },
                        // exibe o marker fixo quando o mouse sai no gráfico
                        mouseOut: (event) => {

                            this.showLastPointMarker();
                            this.chartHover = false;
                        },
                    },
                    dataGrouping: {
                        enabled: false,
                    },
                },
            },
            // end plot options

            // responsive definitions
            responsive: {
                rules: [{
                    condition: {
                        maxWidth: 768,
                    },
                    chartOptions: {
                        xAxis: {
                            maxPadding: .15,
                        },
                        plotOptions : {
                            series : {
                                lineWidth: 2,
                                states: {
                                    hover: {
                                        lineWidth: 2,
                                    },
                                },
                            },
                        },
                    },
                },
                {
                    condition: {
                        maxWidth: 915,
                    },
                    chartOptions: {
                        xAxis: {
                            maxPadding: .24,
                        },
                    },
                }],
            },
            // end responsive definitions
        });
        this.afterChartPlot();
    }

    /**
     * Callback para ser executado após a plotagem do gráfico
     */
    private afterChartPlot() {
        // Force plotlines update
        this.updatePlottedLine(PlotLine.StopGain, this.auxStopGain);
        this.updatePlottedLine(PlotLine.StopLoss, this.auxStopLoss);
        this.updatePlottedLine(PlotLine.Current);

    }

    /**
     * Ordena os valores
     * @param dateA data 01
     * @param dateB data 02
     */
    private sortValuesMapFunction(dateA, dateB): number {
        const date1: any = new Date(dateB.quoteDate);
        const date2: any = new Date(dateA.quoteDate);

        return date2 - date1;
    }

    /**
     * Normaliza os dados
     * @param item item de cotação do quote history
     */
    private normalizeData(item): number[] {
        const date = new Date(item.quoteDate);
        return [date.getTime(), item.valueLastTrade];
    }

    /**
     * Posicionador da tooltip
     */
    private tooltipPositioner(): any {
        return function(labelWidth, labelHeight, point) {
            // muito para esquerda
            let x = point.plotX < 100 ? point.plotX : point.plotX - 50;
            // muito para direita
            x = point.plotX > this.chart.containerWidth - 100 ? x - 50 : x;
            return { x, y: 0 };
        };
    }

    /**
     * Formatter para tooltip exibida no gráfico quando o mouse é posicionado sobre ele
     */
    private tooltipFormatter(): any {
        const interval = this.interval;
        const moneyOrPointsFormatter = this.getMoneyOrPointFormatter(this.currencyPipe, this.decimalPipe);

        return function() {
            return `<div class='chart-tooltip font-small--regular'> ${getTemplateContent(this.x, this.y)} </div>`;

            function getTemplateContent(x, y) {

                switch (interval) {
                    case StockTrackingChartInterval.OneHour:
                    case StockTrackingChartInterval.OneDay:
                        return `<span><b>${moneyOrPointsFormatter(y)} &nbsp;</b> ${highcharts.dateFormat('%H:%M', x)}</span>`;

                    case StockTrackingChartInterval.OneWeek:
                    case StockTrackingChartInterval.OneMonth:
                    case StockTrackingChartInterval.TwoMonths:
                        return `<span><b>${moneyOrPointsFormatter(y)} &nbsp;</b> ${highcharts.dateFormat('%e de %b', x)}</span>`;

                    default:
                        return `<span><b>${moneyOrPointsFormatter(y)} &nbsp;</b> ${highcharts.dateFormat('%e de %b de %Y', x)}</span>`;
                }
            }
        };
    }

    /**
     * Closure para formatar valores monetários (reais ou pontos)
     * @param currencyPipe pipe para formatação de dados monetários
     * @param decimalPipe pipe para formação de pontos (decimais)
     */
    private getMoneyOrPointFormatter(currencyPipe: CurrencyPipe, decimalPipe: DecimalPipe) {

        const monetary = this.market !== OrderMarketTypeEnum.Future;

        return (value: any) => {
            return monetary ? currencyPipe.transform(value, 'BRL', true) : decimalPipe.transform(value, '3.2-5') + ` pts`;
        };
    }

    /**
     * Formater para os labels do eixo y
     * @param decimalPipe pipe para formatação de númeroes
     */
    private yAxisFormatter(decimalPipe: DecimalPipe): () => any {

        const formatYValue = (value: number) => {
            if (this.market !== OrderMarketTypeEnum.Future) {
               const money = this.currencyPipe.transform(value, 'BRL', true);
               return `${money}`.replace(/R\$\s/gi, '');
            }
            return this.decimalPipe.transform(value, '3.2-5');
        };

        return function() {
            return `<span class="font-small--regular">${formatYValue(this.value)}</span>`;
        };
    }

    /**
     * Monta e retorna um label de uma plotline
     * @param y valor atual do ativo
     * @param color cor da plotline
     * @param type tipo da plotline: 'gain'| 'stop'| 'current'
     * @param additionalClass classe adicional para formatação
     */
    private getPlotLineLabel(y: number, color: string, template: string, type: PlotLine, additionalClass: string = '') {
        return {
            type,
            color,
            width: 2,
            dashStyle: 'shortdash',
            value: y,
            label: {
                text: this.getLineLabel( y, template, type, additionalClass),
                align: 'right',
                useHTML: true,
            },
        };
    }

    /**
     * Calcula o valor máximo (limite) do eixo y  (considera os valores do stop gain e o maior valor na série)
     */
    private computeYAxisMaxValue() {
        if (!this.planned) return null;
        const quote = this.auxQuote !== undefined ? this.auxQuote : -Infinity;
        const maxSeriesValue = Math.max( ...this.values.filter((values) => values[1] != null)
            .map((i) => i[1]));
        return Math.max(...[maxSeriesValue, (this.reverse ? this.stopLoss : this.stopGain)]) + Math.abs(this.stopGain - this.stopLoss) / 5;
    }

    /**
     * Computa o valor mínimo para o eixc y do gráfico (considera o menor valor na série e o stop loss)
     */
    private computeYAxisMinValue() {
        if (!this.planned) return null;

        const minSeriesValue = Math.min( ...this.values.filter((values) => values[1] != null)
            .map((i) => i[1]));

        // return Math.min(...[minSeriesValue, (this.reverse? this.stopGain : this.stopLoss)]) - ADITIONAL_CHART_SERIES_SPACING_NEGATIVE;
        return Math.min(...[minSeriesValue, (this.reverse ? this.stopGain : this.stopLoss)]) - Math.abs(this.stopGain - this.stopLoss) / 5;
    }

  private isValidChartInstance() {
    if (!this.chartInstance || !this.chartInstance.series || !this.chartInstance.series.length ) {
        return false;
    } else {
        return true;
    }
  }

}
